import { useWaitlist } from "@clerk/react";
import { useState } from "react";

import { AuthScreen } from "@/components/auth/auth-screen";
import { LegalConsent } from "@/components/auth/legal-consent";
import { PrimaryButton } from "@/components/auth/primary-button";
import { SuccessState } from "@/components/auth/success-state";
import { TextField } from "@/components/auth/text-field";
import { TextLink } from "@/components/auth/text-link";
import { authPath, emailLooksComplete } from "@/lib/auth-helpers";
import { clerkFailureMessage } from "@/lib/clerk-errors";

const ALREADY_WAITING = "This email is already on the waitlist.";

const WaitlistPage = () => {
  const { fetchStatus, waitlist } = useWaitlist();
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [joined, setJoined] = useState(false);

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    if (!emailLooksComplete(email)) {
      setError("Enter a valid email address.");
      return;
    }
    setPending(true);
    const { error: joinError } = await waitlist.join({
      emailAddress: email.trim(),
    });
    setPending(false);
    if (joinError) {
      setError(
        joinError.code === "form_identifier_exists"
          ? ALREADY_WAITING
          : clerkFailureMessage(joinError)
      );
      return;
    }
    setJoined(true);
  };

  if (joined) {
    return (
      <SuccessState
        subtitle="We’ll email you when a place is available."
        title="You’re on the waitlist"
      />
    );
  }

  return (
    <AuthScreen
      subtitle="Enter your email address to request access."
      title="Join the waitlist"
    >
      <form className="flex flex-col gap-8" onSubmit={onSubmit}>
        <TextField
          autoComplete="email"
          error={error}
          id="waitlist-email"
          label="Email"
          onChange={(event) => {
            setEmail(event.target.value);
            setError(null);
          }}
          placeholder="name@company.com"
          type="email"
          value={email}
        />
        <div className="flex flex-col gap-3.5">
          <PrimaryButton
            busy={pending || fetchStatus === "fetching"}
            type="submit"
          >
            Join Waitlist
          </PrimaryButton>
          <LegalConsent />
        </div>
      </form>
      <p className="m-0 flex flex-wrap gap-1.5 border-n-200 border-t pt-6 text-[15px] text-n-600">
        <span>Already have an account?</span>
        <TextLink to={authPath("/login")}>Sign in</TextLink>
      </p>
    </AuthScreen>
  );
};

export default WaitlistPage;
